import { useState } from "react";
import { Lock, Loader2, Share2, Infinity as InfinityIcon } from "lucide-react";
import ShareModal from "@/components/ShareModal";

function renderFormatted(text) {
    if (!text) return null;
    const lines = text.split(/\r?\n/);
    const blocks = [];
    let currentList = null;
    let currentPara = [];

    const flushPara = () => {
        if (currentPara.length) {
            blocks.push({ type: "p", content: currentPara.join(" ") });
            currentPara = [];
        }
    };
    const flushList = () => {
        if (currentList && currentList.items.length) {
            blocks.push(currentList);
        }
        currentList = null;
    };

    for (const raw of lines) {
        const line = raw.trim();
        if (!line) {
            flushPara();
            flushList();
            continue;
        }
        const heading = line.match(/^\*\*(.+?)\*\*\s*:?$/);
        if (heading) {
            flushPara();
            flushList();
            blocks.push({ type: "h", content: heading[1] });
            continue;
        }
        if (line.startsWith("- ") || line.startsWith("• ")) {
            flushPara();
            if (!currentList) currentList = { type: "ul", items: [] };
            currentList.items.push(line.replace(/^[-•]\s+/, ""));
            continue;
        }
        flushList();
        currentPara.push(line);
    }
    flushPara();
    flushList();

    return blocks.map((b, i) => {
        if (b.type === "h") return <strong key={i}>{b.content}</strong>;
        if (b.type === "p") return <p key={i}>{b.content}</p>;
        if (b.type === "ul")
            return (
                <ul key={i}>
                    {b.items.map((it, j) => (
                        <li key={j}>{it}</li>
                    ))}
                </ul>
            );
        return null;
    });
}

export default function AiResponse({
    message,
    onUnlock,
    onSubscribe,
    unlocking,
    subscribing,
    passActive,
}) {
    const isLocked = !message.is_unlocked;
    const text = message.content;
    const [shareOpen, setShareOpen] = useState(false);

    return (
        <>
            <div
                className="max-w-[92%] w-full sm:max-w-[85%] bg-rt-card rounded-2xl rounded-bl-md px-5 sm:px-6 py-5 border border-white/5"
                data-testid="ai-bubble"
            >
                <div
                    className={`rt-ai-content ${isLocked ? "rt-paywall-fade" : ""}`}
                    data-testid={
                        isLocked ? "ai-preview-content" : "ai-full-content"
                    }
                >
                    {renderFormatted(text)}
                </div>

                {isLocked && (
                    <div className="relative -mt-2">
                        <div className="mt-4 flex flex-col gap-3 p-4 rounded-2xl border border-rt-border bg-rt-surface/80 backdrop-blur-md">
                            <div className="flex items-start gap-3">
                                <div className="mt-0.5 shrink-0 w-8 h-8 rounded-full bg-[#B392F0]/15 border border-[#B392F0]/25 flex items-center justify-center">
                                    <Lock className="w-4 h-4 text-[#B392F0]" />
                                </div>
                                <div>
                                    <div className="font-serif text-base tracking-tight">
                                        There&rsquo;s more here.
                                    </div>
                                    <div className="text-xs text-rt-muted mt-0.5">
                                        Options, likely outcomes, and a grounded
                                        suggestion.
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-2">
                                <button
                                    onClick={() => onUnlock(message.id)}
                                    disabled={unlocking || subscribing}
                                    data-testid="unlock-answer-btn"
                                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#B392F0] hover:bg-[#C8B1F5] disabled:opacity-60 text-black font-medium text-sm px-5 py-2.5 transition-all duration-200 hover:-translate-y-[1px]"
                                >
                                    {unlocking ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : null}
                                    Unlock Full Answer – $5
                                </button>
                                <button
                                    onClick={() => onSubscribe?.()}
                                    disabled={subscribing || unlocking}
                                    data-testid="subscribe-pass-btn"
                                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-rt-border bg-rt-card hover:bg-rt-card/80 disabled:opacity-60 text-rt-text text-sm px-5 py-2.5 transition-all duration-200 hover:-translate-y-[1px]"
                                >
                                    {subscribing ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <InfinityIcon className="w-4 h-4 text-[#B392F0]" />
                                    )}
                                    Unlock everything · $9.99/mo
                                </button>
                            </div>
                            <div className="text-[11px] text-rt-dim text-center">
                                Monthly pass unlocks every answer in this
                                session for 30 days.
                            </div>
                        </div>
                    </div>
                )}

                {!isLocked && !passActive && (
                    <div className="mt-5 flex items-center justify-end">
                        <button
                            onClick={() => setShareOpen(true)}
                            className="inline-flex items-center gap-1.5 text-xs text-rt-muted hover:text-rt-text transition"
                            data-testid="share-insight-btn"
                        >
                            <Share2 className="w-3.5 h-3.5" />
                            Share this insight
                        </button>
                    </div>
                )}

                {!isLocked && passActive && (
                    <div className="mt-5 flex items-center justify-end">
                        <button
                            onClick={() => setShareOpen(true)}
                            className="inline-flex items-center gap-1.5 text-xs text-rt-muted hover:text-rt-text transition"
                            data-testid="share-insight-btn"
                        >
                            <Share2 className="w-3.5 h-3.5" />
                            Share this insight
                        </button>
                    </div>
                )}
            </div>

            {shareOpen && (
                <ShareModal
                    messageId={message.id}
                    onClose={() => setShareOpen(false)}
                />
            )}
        </>
    );
}
